import React from 'react';
import {
  applyLayout,
  DEFAULT_LAYOUT_ID,
  DESKTOP_LAYOUT_MEDIA_QUERY,
  getAutoLayoutId,
  isValidLayoutId,
  LayoutId,
  readForcedLayoutId,
} from './layouts';
import { LayoutContext, type LayoutContextValue } from './LayoutContext';

const sanitizeLayoutId = (id?: string | null): LayoutId => {
  if (id && isValidLayoutId(id)) {
    return id;
  }
  return DEFAULT_LAYOUT_ID;
};

interface LayoutProviderProps {
  children: React.ReactNode;
  initialLayoutId?: LayoutId;
}

export function LayoutProvider({ children, initialLayoutId }: LayoutProviderProps) {
  const storedLayoutId = React.useMemo(
    () => sanitizeLayoutId(initialLayoutId ?? readForcedLayoutId() ?? getAutoLayoutId()),
    [initialLayoutId],
  );
  const [layoutId, setLayoutId] = React.useState<LayoutId>(storedLayoutId);

  React.useEffect(() => {
    applyLayout(layoutId);
  }, [layoutId]);

  React.useEffect(() => {
    if (initialLayoutId || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia(DESKTOP_LAYOUT_MEDIA_QUERY);
    const sync = () => {
      const forced = readForcedLayoutId();
      setLayoutId(sanitizeLayoutId(forced ?? getAutoLayoutId()));
    };
    sync();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', sync);
      return () => mediaQuery.removeEventListener('change', sync);
    }
    mediaQuery.addListener(sync);
    return () => mediaQuery.removeListener(sync);
  }, [initialLayoutId]);

  const value = React.useMemo<LayoutContextValue>(
    () => ({ layoutId }),
    [layoutId],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}
